import { useEffect, useState } from "react";
import { doc, onSnapshot } from "firebase/firestore";
import { RING_DAYS, RING_DAY_LABELS, RingSettingsSchema, clockIn, isOpenAt, paths, type RingDay, type RingHours } from "@clevis/shared-types";
import { Button, Card, CardTitle, Input, Label, Select } from "@clevis/shared-ui";
import { db } from "../lib/firebase";
import { call } from "../lib/callFunction";

// Setup → Clevis Ring → Working hours. A phone system rings the office by day
// and the on-call phone by night; a main line forwarded to Clevis needs the
// same, or an evening call rings an empty office. One weekly schedule in the
// company's time zone; each number says what to ring after hours.

const TIMEZONES = ["America/Los_Angeles", "America/Phoenix", "America/Denver", "America/Chicago", "America/New_York", "Pacific/Honolulu", "America/Anchorage"];
const HHMM = /^\d{2}:\d{2}$/;

function clockLabel(hhmm: string): string {
  const [h, m] = hhmm.split(":").map(Number);
  const hour12 = ((h ?? 0) + 11) % 12 + 1;
  return `${hour12}:${String(m ?? 0).padStart(2, "0")} ${(h ?? 0) < 12 ? "AM" : "PM"}`;
}

export function RingHoursCard({ tenantId }: { tenantId: string }) {
  const [saved, setSaved] = useState<RingHours | null>(null);
  const [draft, setDraft] = useState<RingHours | null>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const [now, setNow] = useState(Date.now());

  useEffect(
    () =>
      onSnapshot(doc(db, paths.tenantRingSettings(tenantId)), (snap) => {
        setSaved(RingSettingsSchema.parse(snap.data() ?? {}).hours);
        setDraft(null);
      }),
    [tenantId],
  );
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  if (!saved) return null;
  const h = draft ?? saved;
  const tz = h.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone;
  const set = (patch: Partial<RingHours>) => setDraft({ ...h, ...patch });
  const setDay = (d: RingDay, patch: Partial<RingHours[RingDay]>) => setDraft({ ...h, [d]: { ...h[d], ...patch } });
  const clock = h.enabled ? clockIn(tz, now) : null;
  const openNow = isOpenAt({ ...h, timezone: tz }, now);

  async function save() {
    setBusy(true);
    setNote("");
    try {
      const r = await call<{ openNow: boolean }>("setRingHours", { tenantId, hours: { ...h, timezone: h.enabled ? tz : h.timezone } });
      setNote(!h.enabled ? "Saved. Every number rings its usual line around the clock." : r.openNow ? "Saved. You're open right now." : "Saved. You're closed right now, so after-hours lines ring.");
    } catch (err) {
      setNote((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card className="space-y-2">
      <CardTitle>Working hours</CardTitle>
      <p className="text-xs text-steel">
        Outside these hours, a number with an "after hours" line sends the call there, the on-call phone, instead of the office. A number without one rings
        the same place around the clock. Calls are logged and popped either way.
      </p>
      <label className="flex items-center gap-2 text-sm text-ink">
        <input type="checkbox" checked={h.enabled} onChange={(e) => set({ enabled: e.target.checked })} />
        Use working hours for Clevis Ring numbers
      </label>
      {h.enabled ? (
        <>
          <div className="flex flex-wrap items-end gap-3">
            <div>
              <Label>Time zone</Label>
              <Select value={tz} onChange={(e) => set({ timezone: e.target.value })}>
                {[...new Set([tz, ...TIMEZONES])].map((z) => (
                  <option key={z} value={z}>
                    {z.replace(/_/g, " ")}
                  </option>
                ))}
              </Select>
            </div>
            {clock ? (
              <span className="pb-1.5 text-xs text-steel">
                Right now there it's {clockLabel(clock.hhmm)}: {openNow ? "open" : "closed"}.
              </span>
            ) : null}
          </div>
          <div className="divide-y divide-steel/10">
            {RING_DAYS.map((d) => (
              <div key={d} className="flex flex-wrap items-center gap-3 py-1.5 text-sm">
                <label className="flex w-32 items-center gap-2 text-ink">
                  <input type="checkbox" checked={h[d].open} onChange={(e) => setDay(d, { open: e.target.checked })} />
                  {RING_DAY_LABELS[d]}
                </label>
                {h[d].open ? (
                  <>
                    <Input type="time" className="w-32 py-0.5 text-xs" value={h[d].start} onChange={(e) => HHMM.test(e.target.value) && setDay(d, { start: e.target.value })} />
                    <span className="text-xs text-steel">to</span>
                    <Input type="time" className="w-32 py-0.5 text-xs" value={h[d].end} onChange={(e) => HHMM.test(e.target.value) && setDay(d, { end: e.target.value })} />
                  </>
                ) : (
                  <span className="text-xs text-steel">Closed</span>
                )}
              </div>
            ))}
          </div>
        </>
      ) : null}
      <div className="flex items-center gap-2">
        <Button busy={busy} disabled={draft === null} onClick={() => void save()}>
          Save hours
        </Button>
        {note ? <span className="text-xs text-steel">{note}</span> : null}
      </div>
    </Card>
  );
}
